import { createContext, useContext, useMemo, useState } from "react";
import {
  MutationFunction,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { exists } from "i18next";
import { ApiError, SuccessFetchListResponse } from "@/services/api";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { ReadProjectRole, WriteProjectRole } from "@/types/projectRoleType";
import { ProjectRolesService } from "@/modules/projects/service/projectRole";
import useQueryData from "@/hooks/useQueryData";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";

interface ProjectRoleContextType {
  projectId: string;
  data: ReadProjectRole[];
  isUpsertRoleDialogOpen: boolean;
  setIsUpsertRoleDialogOpen: (value: boolean) => void;
  inFetching: boolean;
  projectRole?: ReadProjectRole;
  setProjectRole: (value?: ReadProjectRole) => void;
  projectRoleMutation: UseMutationResult<
    ReadProjectRole,
    ApiError,
    WriteProjectRole
  >;
}

const ProjectRoleContext = createContext<ProjectRoleContextType | undefined>(
  undefined,
);

export const ProjectRoleContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isUpsertRoleDialogOpen, setIsUpsertRoleDialogOpen] = useState(false);
  const [projectRole, setProjectRole] = useState<ReadProjectRole | undefined>();
  const { t } = useTranslation();
  const { query } =
    useDataTableQuery<SuccessFetchListResponse<ReadProjectRole>>();
  const projectId = useProjectId();
  const data = useQueryData(query);

  const mutateProjectRole: MutationFunction<
    ReadProjectRole,
    WriteProjectRole
  > = async (values) => {
    if (projectRole) {
      return ProjectRolesService.patch(projectId, projectRole.id, values);
    }

    return ProjectRolesService.post(projectId, values);
  };

  const projectRoleMutation = useMutation<
    ReadProjectRole,
    ApiError,
    WriteProjectRole
  >({
    mutationFn: mutateProjectRole,
    onSuccess: () => {
      toast({
        title: projectRole?.id
          ? t("projectRoles.success.updated")
          : t("projectRoles.success.created"),
      });
      setProjectRole(undefined);
      setIsUpsertRoleDialogOpen(false);
      query.refetch();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: projectRole?.id
          ? t("projectRoles.errors.updated")
          : t("projectRoles.errors.created"),
        description,
        variant: "destructive",
      });
    },
  });

  const inFetching = useMemo(() => {
    return query.isFetching || projectRoleMutation.isPending;
  }, [query.isFetching, projectRoleMutation.isPending]);

  const value: ProjectRoleContextType = {
    projectId,
    projectRoleMutation,
    data,
    isUpsertRoleDialogOpen,
    setIsUpsertRoleDialogOpen,
    inFetching,
    projectRole,
    setProjectRole,
  };

  return (
    <ProjectRoleContext.Provider value={value}>
      {children}
    </ProjectRoleContext.Provider>
  );
};

export const useProjectRoleContext = () => {
  const context = useContext(ProjectRoleContext);
  if (!context) {
    throw new Error(
      "useProjectRoleContext must be used within a ProjectRoleContextProvider",
    );
  }
  return context;
};
